import { IModel, IAttribute } from '../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedService } from '../linkedservice/linkedservice';

import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { validateAKVLinkedserviceReference } from 'app/shared/utils/linkedservice-validation-util';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { format } from 'app/shared/utils/string.util';
import { oracleAdditionalPropertiesAdapts, OracleAdditionalPropertiesKeyType } from 'app/registries/consts/oracle-const';
import { Owned, ParameterConfig } from 'shell-models-lib';
import { driverVersions } from 'app/registries/consts/tabular-consts';
import { LinkedServiceUtils } from './linked-service.utils';

@Metamodel.model({
    name: adfModelTypeMap.oracle.linkedServiceType,
    display: [
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Oracle_DisplayText),
        format(stringResources.ModelName_LinkedServicesTemplate, stringResources.Oracle_DisplayText)
    ]
})
export class OracleLinkedService extends LinkedService {

    public static readonly MODEL: IModel;
    public static readonly HOST_NAME: IAttribute;
    public static readonly PORT: IAttribute;
    public static readonly SID: IAttribute;
    public static readonly SERVICE_NAME: IAttribute;
    public static readonly SERVER: IAttribute;
    public static readonly USER_NAME: IAttribute;
    public static readonly PASSWORD: IAttribute;

    private _defaultAdditionalPropertyValues: Array<{ key: OracleAdditionalPropertiesKeyType, value: string }> = [
        {
            key: 'encryptionClient',
            value: 'accepted'
        },
        {
            key: 'encryptionTypesClient',
            value: '(AES128, AES192, AES256, 3DES112, 3DES168)'
        },
        {
            key: 'cryptoChecksumClient',
            value: 'accepted'
        },
        {
            key: 'cryptoChecksumTypesClient',
            value: '(SHA1, SHA256, SHA384, SHA512)'
        }
    ];

    @Metamodel.attribute({
        display: stringResources.ModelName_Host,
        association: 'HOST_NAME',
        templatable: true
    })
    public hostName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Port,
        association: 'PORT',
        templatable: true
    })
    public port?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Sid,
        association: 'SID',
        templatable: true
    })
    public sid?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_ServiceName,
        association: 'SERVICE_NAME',
        templatable: true
    })
    public serviceName?: string;

    public resourceType: 'sid' | 'serviceName' = 'sid';

    @Metamodel.attribute({
        display: stringResources.Server,
        association: 'SERVER',
        templatable: true
    })
    public server?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_UserName,
        association: 'USER_NAME',
        templatable: true
    })
    public userName?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_Password,
        constraint: {
            validator: validateAKVLinkedserviceReference
        },
        association: 'PASSWORD',
        templatable: true,
        accepts: ['String']
    })
    public password?: string | AzureKeyVault;

    public enableAKV?: boolean = false;
    @Metamodel.attribute({
        constraint: {
            validator: validateAKVLinkedserviceReference
        }
    })
    public referedAKV?: AzureKeyVault;

    @Metamodel.attribute({
        display: stringResources.VersionLabel
    })
    public version?: string;

    @Metamodel.attribute({
        display: stringResources.ModelName_AuthenticationType
    })
    public authenticationType?: string;

    public parameterizableAdditionalFields: Array<ParameterConfig<OracleAdditionalPropertiesKeyType>>;

    public get showConvertDecimalToIntegerControl(): boolean {
        if (__IS_FABRIC_DPE__) {
            return false;
        }
        if (this.version !== driverVersions.v2) {
            return true;
        }
        const config = this.parameterizableAdditionalFields?.find((f) => f.name === 'supportV1DataTypes');
        if (!config) {
            return false;
        }
        return config.value === true || !!ParameterConfig.VALUE.templateValue(config);
    }

    public get supportV1DataTypesIsParameterizable(): boolean {
        if (__IS_FABRIC_DPE__) {
            return false;
        }
        const config = this.parameterizableAdditionalFields?.find((f) => f.name === 'supportV1DataTypes');
        if (!config) {
            return false;
        }
        return !!ParameterConfig.VALUE.templateValue(config);
    }

    constructor(name?: string, description?: string) {
        super(name, description);
        this.version = driverVersions.v2;
        if (!__IS_FABRIC_DPE__) {
            this.parameterizableAdditionalFields = [];
            this._defaultAdditionalPropertyValues.forEach((item) => {
                const paramConfig = Owned.create<ParameterConfig<OracleAdditionalPropertiesKeyType>>(ParameterConfig.MODEL, this);
                paramConfig.name = item.key;
                paramConfig.value = item.value;
                paramConfig.type = oracleAdditionalPropertiesAdapts[item.key];
                this.parameterizableAdditionalFields.push(paramConfig);
            });
        }
    }

    public convertV1toV2(context: { inlineDataset?: boolean }): OracleLinkedService {
        const copy = LinkedServiceUtils.commonV1ToV2(context, this) as OracleLinkedService;
        if (this.enableAKV) {
            copy.enableAKV = false;
            delete copy.referedAKV;
        } else if (this.version !== driverVersions.v2) {
            copy.server = this.hostName;
            copy.authenticationType = 'Basic';
            // given it's upgrading from v1, defaulting to support v1 datatypes
            const config = Owned.create<ParameterConfig<OracleAdditionalPropertiesKeyType>>(ParameterConfig.MODEL, copy);
            config.name = 'supportV1DataTypes';
            config.value = true;
            config.type = oracleAdditionalPropertiesAdapts['supportV1DataTypes'];
            copy.parameterizableAdditionalFields?.push(config);
        }
        return copy;
    }
}
